import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bolt, Search, Sparkles, Zap } from "lucide-react";
import {
  PLATFORMS,
  normalizeUsername,
  validateUsername,
  type CheckResponseData,
  type PlatformDefinition,
  type PlatformId
} from "@handlequest/shared";
import { AIGenerator } from "./components/AIGenerator";
import { PlatformSelector } from "./components/PlatformSelector";
import { ResultGrid } from "./components/ResultGrid";
import { ThemeToggle } from "./components/ThemeToggle";
import { TurnstileWidget } from "./components/TurnstileWidget";
import { useTheme } from "./hooks/useTheme";
import { checkUsername, getPlatforms } from "./lib/api";
import { messages } from "./i18n/messages";

const sampleNames = ["VoidRunner", "NeonAegis", "PixelWarden"];
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";

function usernameFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/check\/([^/]+)$/);
  return match ? decodeURIComponent(match[1] ?? "") : null;
}

export function App() {
  const { theme, toggleTheme } = useTheme();
  const [path, setPath] = useState(window.location.pathname);
  const [username, setUsername] = useState(usernameFromPath(window.location.pathname) ?? "");
  const [platforms, setPlatforms] = useState<PlatformDefinition[]>([...PLATFORMS]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<PlatformId[]>(PLATFORMS.map((platform) => platform.id));
  const [result, setResult] = useState<CheckResponseData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatorOpen, setGeneratorOpen] = useState(window.location.pathname === "/generator");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);
  const autoCheckedPath = useRef<string | null>(null);

  const routeUsername = useMemo(() => usernameFromPath(path), [path]);

  useEffect(() => {
    getPlatforms()
      .then((data) => {
        setPlatforms(data.platforms);
        setSelectedPlatforms(data.platforms.map((platform) => platform.id));
      })
      .catch(() => {
        setPlatforms([...PLATFORMS]);
      });
  }, []);

  useEffect(() => {
    const listener = () => {
      setPath(window.location.pathname);
      setGeneratorOpen(window.location.pathname === "/generator");
    };
    window.addEventListener("popstate", listener);
    return () => window.removeEventListener("popstate", listener);
  }, []);

  const runCheck = useCallback(async (rawUsername: string, pushRoute = true) => {
    const normalized = normalizeUsername(rawUsername);
    const validation = validateUsername(normalized);
    if (!validation.valid) {
      setError(validation.message);
      return;
    }

    setUsername(normalized);
    setError(null);
    setLoading(true);
    if (pushRoute) {
      const nextPath = `/check/${encodeURIComponent(normalized)}`;
      window.history.pushState(null, "", nextPath);
      setPath(nextPath);
    }

    try {
      const activePlatforms = selectedPlatforms.length ? selectedPlatforms : platforms.map((platform) => platform.id);
      const data = await checkUsername(normalized, activePlatforms, turnstileSiteKey ? turnstileToken ?? undefined : undefined);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The availability check failed.");
    } finally {
      setLoading(false);
      if (turnstileSiteKey) {
        setTurnstileToken(null);
        setTurnstileResetSignal((current) => current + 1);
      }
    }
  }, [platforms, selectedPlatforms, turnstileToken]);

  useEffect(() => {
    if (!routeUsername || autoCheckedPath.current === path) return;
    autoCheckedPath.current = path;
    void runCheck(routeUsername, false);
  }, [path, routeUsername, runCheck]);

  function togglePlatform(platformId: PlatformId) {
    setSelectedPlatforms((current) => {
      const next = current.includes(platformId)
        ? current.filter((id) => id !== platformId)
        : [...current, platformId];
      return next.length ? next : platforms.map((platform) => platform.id);
    });
  }

  function openGenerator(pushRoute = false) {
    setGeneratorOpen(true);
    if (pushRoute) {
      window.history.pushState(null, "", "/generator");
      setPath("/generator");
    }
  }

  function closeGenerator() {
    setGeneratorOpen(false);
    if (path === "/generator") {
      window.history.pushState(null, "", "/");
      setPath("/");
    }
  }

  return (
    <>
      <header className="topbar">
        <div className="container nav">
          <a className="brand" href="/" onClick={(event) => {
            event.preventDefault();
            window.history.pushState(null, "", "/");
            setPath("/");
          }}>
            <span className="bolt"><Bolt aria-hidden="true" /></span>
            {messages.brand}
          </a>
          <nav className="navlinks" aria-label="Primary navigation">
            <a href="#platforms">Platforms</a>
            <a href="#results">Results</a>
            <button className="ai-button" type="button" onClick={() => openGenerator(true)}>
              <Sparkles aria-hidden="true" />AI names
            </button>
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
          </nav>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="container hero-inner">
            <p className="eyebrow">Multi-platform gamer tag scanner</p>
            <h1>{messages.heroTitle}</h1>
            <p>{messages.heroCopy}</p>
            <form className="search-shell" onSubmit={(event) => {
              event.preventDefault();
              void runCheck(username);
            }}>
              <label className="search-input-wrap">
                <Search aria-hidden="true" />
                <input
                  className="search-input"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="try ShadowCircuit"
                  spellCheck={false}
                  autoComplete="off"
                  maxLength={16}
                />
              </label>
              <button className="primary-button" type="submit" disabled={loading || Boolean(turnstileSiteKey && !turnstileToken)}>
                <Zap aria-hidden="true" />Check
              </button>
            </form>
            <TurnstileWidget
              onToken={setTurnstileToken}
              resetSignal={turnstileResetSignal}
              siteKey={turnstileSiteKey}
              theme={theme}
            />
            {error && <p className="error-line">{error}</p>}
            <div className="hero-actions">
              {sampleNames.map((sample) => (
                <button className="ghost-button" type="button" key={sample} onClick={() => void runCheck(sample)}>
                  {sample}
                </button>
              ))}
              <button className="ai-button" type="button" onClick={() => openGenerator(true)}>
                <Sparkles aria-hidden="true" />Generate names
              </button>
            </div>
          </div>
        </section>

        <section className="section" id="platforms">
          <div className="container">
            <PlatformSelector
              platforms={platforms}
              selected={selectedPlatforms}
              onToggle={togglePlatform}
              onSelectAll={() => setSelectedPlatforms(platforms.map((platform) => platform.id))}
            />
          </div>
        </section>

        <section className="section" id="results">
          <div className="container">
            <ResultGrid
              result={result}
              loading={loading}
              selectedPlatforms={selectedPlatforms}
              platforms={platforms}
            />
          </div>
        </section>
      </main>

      <AIGenerator
        open={generatorOpen}
        onClose={closeGenerator}
        onCheck={(nextUsername) => {
          closeGenerator();
          void runCheck(nextUsername);
        }}
        turnstileSiteKey={turnstileSiteKey}
        theme={theme}
      />
    </>
  );
}
